import React, {
  type AnchorHTMLAttributes,
  type ForwardRefExoticComponent,
  type MouseEvent,
  type ReactNode,
} from "react";
import c from "classnames/bind";

import styles from "./card-trigger-item.module.scss";

const cx = c.bind(styles);

export type CardTriggerItemProps = {
  ["data-testid"]?: string;
  ["aria-label"]?: string;
  disabled?: boolean;
  href?: string;
  onClick?: () => unknown;
  children: ReactNode;
  linkElement?: ForwardRefExoticComponent<AnchorHTMLAttributes<HTMLAnchorElement>> | "a";
};

const rootClassName = "purpur-card-trigger-item";

export const CardTriggerItem = ({
  ["data-testid"]: dataTestId = "purpur-card-trigger-item",
  ["aria-label"]: ariaLabel,
  disabled = false,
  href,
  onClick,
  children,
  linkElement: LinkElement = "a",
}: CardTriggerItemProps) => {
  const handleClick = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
    if (disabled) {
      e.preventDefault();
    } else if (onClick) {
      onClick();
    }
  };

  const additionalNonHashedClasses = `${rootClassName} ${
    disabled ? `${rootClassName}--disabled` : ""
  }`;

  return href ? (
    <LinkElement
      aria-disabled={disabled}
      aria-label={ariaLabel}
      className={`${cx([
        rootClassName,
        `${rootClassName}--link`,
        {
          [`${rootClassName}--disabled`]: disabled,
        },
      ])} ${additionalNonHashedClasses}`}
      data-testid={dataTestId}
      href={disabled ? undefined : href}
      tabIndex={disabled ? 0 : undefined}
      onClick={handleClick}
    >
      {children}
    </LinkElement>
  ) : (
    <button
      aria-disabled={disabled}
      aria-label={ariaLabel}
      className={`${cx([
        rootClassName,
        `${rootClassName}--button`,
        {
          [`${rootClassName}--disabled`]: disabled,
        },
      ])} ${additionalNonHashedClasses}`}
      data-testid={dataTestId}
      type="button"
      onClick={handleClick}
    >
      {children}
    </button>
  );
};
